import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ProtectedElement from './ProtectedElement';
import axios from 'axios';
import Modal from 'react-modal';
import DocumentViewerModal from './DocumentViewerModal';
import './FileExplorer.css';

const BASE_URL = process.env.REACT_APP_SERVER_URL || '';

// --- Utility ---
function isPermissionDenied(error) {
    if (!error) return false;
    const status = error.response?.status;
    if (status === 401 || status === 403) return true;
    const msg = (error.response?.data?.error || error.response?.data?.message || error.message || '').toLowerCase();
    if (!msg) return false;
    if (msg.includes('permission') && msg.includes('denied')) return true;
    if (msg.includes('not authorized') || msg.includes('forbidden')) return true;
    return false;
}

const formatFileSize = (bytes) => {
    if (bytes == null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

// --- Icons ---
const FolderIcon = ({ size = 16 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="currentColor" className="fe-icon fe-icon-folder" viewBox="0 0 16 16">
        <path d="M9.828 3h3.982a2 2 0 0 1 1.992 2.181l-.637 7A2 2 0 0 1 13.174 14H2.826a2 2 0 0 1-1.991-1.819l-.637-7a1.99 1.99 0 0 1 .342-1.31L.5 3a2 2 0 0 1 2-2h3.672a2 2 0 0 1 1.414.586l.828.828A2 2 0 0 0 9.828 3"/>
    </svg>
);
const DefaultFileIcon = ({ size = 16 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="currentColor" className="fe-icon fe-icon-file" viewBox="0 0 16 16">
        <path d="M9.293 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.707A1 1 0 0 0 13.707 4L10 .293A1 1 0 0 0 9.293 0M9.5 3.5v-2l3 3h-2a1 1 0 0 1-1-1M4.5 9a.5.5 0 0 1 0-1h7a.5.5 0 0 1 0 1zm0 2a.5.5 0 0 1 0-1h7a.5.5 0 0 1 0 1zm0 2a.5.5 0 0 1 0-1h4a.5.5 0 0 1 0 1z"/>
    </svg>
);
const PdfIcon = ({ size = 16 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="currentColor" className="fe-icon fe-icon-pdf" viewBox="0 0 16 16">
        <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
        <path d="M4.603 14.087a.8.8 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.7 7.7 0 0 1 1.482-.645 20 20 0 0 0 1.062-2.227 7.3 7.3 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.188-.012.396-.047.614-.084.51-.27 1.134-.52 1.794a11 11 0 0 0 .98 1.686 5.8 5.8 0 0 1 1.334.05c.364.066.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.86.86 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a6.1 6.1 0 0 1-.911-.95 11.7 11.7 0 0 0-1.997.406 11.3 11.3 0 0 1-1.02 1.51c-.292.35-.609.656-.927.787a.8.8 0 0 1-.58.029z"/>
    </svg>
);
const WordIcon = ({ size = 16 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="currentColor" className="fe-icon fe-icon-word" viewBox="0 0 16 16">
        <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
        <path d="M4.5 12.5A.5.5 0 0 1 5 12h3a.5.5 0 0 1 0 1H5a.5.5 0 0 1-.5-.5m0-2A.5.5 0 0 1 5 10h6a.5.5 0 0 1 0 1H5a.5.5 0 0 1-.5-.5m0-2A.5.5 0 0 1 5 8h6a.5.5 0 0 1 0 1H5a.5.5 0 0 1-.5-.5m0-2A.5.5 0 0 1 5 6h6a.5.5 0 0 1 0 1H5a.5.5 0 0 1-.5-.5"/>
    </svg>
);
const SpreadsheetIcon = ({ size = 16 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="currentColor" className="fe-icon fe-icon-spreadsheet" viewBox="0 0 16 16">
        <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
        <path d="M3 10.5v-4h10v4zm1-3v.75h2V7.5zm0 1.25v.75h2V8.75zm0 1.25v.75h2V10zm3-2.5v.75h2V7.5zm0 1.25v.75h2V8.75zm0 1.25v.75h2V10zm3-2.5v.75h1V7.5zm0 1.25v.75h1V8.75zm0 1.25v.75h1V10z"/>
    </svg>
);
const PresentationIcon = ({ size = 16 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="currentColor" className="fe-icon fe-icon-presentation" viewBox="0 0 16 16">
        <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
        <path d="M4 6.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-.5.5h-7a.5.5 0 0 1-.5-.5z"/>
    </svg>
);
const ImageIcon = ({ size = 16 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="currentColor" className="fe-icon fe-icon-image" viewBox="0 0 16 16">
        <path d="M6.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0"/>
        <path d="M2.002 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2zm12 1a1 1 0 0 1 1 1v6.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12V3a1 1 0 0 1 1-1z"/>
    </svg>
);
const CodeIcon = ({ size = 16 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="currentColor" className="fe-icon fe-icon-code" viewBox="0 0 16 16">
        <path d="M14 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1zM2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2z"/>
        <path d="M6.854 4.646a.5.5 0 0 1 0 .708L4.207 8l2.647 2.646a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 0 1 .708 0m2.292 0a.5.5 0 0 0 0 .708L11.793 8l-2.647 2.646a.5.5 0 0 0 .708.708l3-3a.5.5 0 0 0 0-.708l-3-3a.5.5 0 0 0-.708 0"/>
    </svg>
);
const ArchiveIcon = ({ size = 16 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="currentColor" className="fe-icon fe-icon-archive" viewBox="0 0 16 16">
        <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
        <path d="M5 7.5V7h1v.5a.5.5 0 0 1-.5.5h0a.5.5 0 0 1-.5-.5m1 1V8h1v.5a.5.5 0 0 1-.5.5h0a.5.5 0 0 1-.5-.5m-1 1V9h1v.5a.5.5 0 0 1-.5.5h0a.5.5 0 0 1-.5-.5m1 1V10h1v.5a.5.5 0 0 1-.5.5h0a.5.5 0 0 1-.5-.5"/>
    </svg>
);
const SortAscIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" className="fe-sort-icon" viewBox="0 0 16 16">
        <path d="m7.247 4.86-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z"/>
    </svg>
);
const SortDescIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" className="fe-sort-icon" viewBox="0 0 16 16">
        <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z"/>
    </svg>
);
const ListViewIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path fillRule="evenodd" d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5"/>
    </svg>
);
const GridViewIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5zm8 0A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5zm-8 8A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5zm8 0A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5z"/>
    </svg>
);

const getFileIcon = (fileName, type, size = 16) => {
    if (type === 'dir') return <FolderIcon size={size} />;
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
        case 'pdf': return <PdfIcon size={size} />;
        case 'doc': case 'docx': return <WordIcon size={size} />;
        case 'xls': case 'xlsx': case 'csv': return <SpreadsheetIcon size={size} />;
        case 'ppt': case 'pptx': return <PresentationIcon size={size} />;
        case 'png': case 'jpg': case 'jpeg': case 'gif': case 'webp': case 'svg': case 'bmp': case 'ico': return <ImageIcon size={size} />;
        case 'py': case 'js': case 'ts': case 'jsx': case 'tsx': case 'css': case 'html': case 'json': case 'xml': case 'java': case 'c': case 'cpp': case 'sh': case 'bat': return <CodeIcon size={size} />;
        case 'zip': case 'tar': case 'gz': case 'rar': case '7z': return <ArchiveIcon size={size} />;
        default: return <DefaultFileIcon size={size} />;
    }
};

// --- Breadcrumbs ---
const Breadcrumbs = ({ currentPath, onNavigate, onDrop, onDragOver }) => {
    const parts = currentPath === '.' ? [] : currentPath.split('/').filter(Boolean);
    return (
        <nav className="fe-breadcrumbs" aria-label="File path">
            <span
                className={`fe-breadcrumb-item ${parts.length > 0 ? 'fe-breadcrumb-clickable' : 'fe-breadcrumb-current'}`}
                onClick={parts.length > 0 ? () => onNavigate('.') : undefined}
                onDragOver={parts.length > 0 ? onDragOver : undefined}
                onDrop={parts.length > 0 ? (e) => onDrop(e, '.') : undefined}
            >
                Home
            </span>
            {parts.map((part, index) => {
                const path = parts.slice(0, index + 1).join('/');
                const isLast = index === parts.length - 1;
                return (
                    <React.Fragment key={path}>
                        <span className="fe-breadcrumb-sep">/</span>
                        <span
                            className={`fe-breadcrumb-item ${!isLast ? 'fe-breadcrumb-clickable' : 'fe-breadcrumb-current'}`}
                            onClick={!isLast ? () => onNavigate(path) : undefined}
                            onDragOver={!isLast ? onDragOver : undefined}
                            onDrop={!isLast ? (e) => onDrop(e, path) : undefined}
                        >
                            {part}
                        </span>
                    </React.Fragment>
                );
            })}
        </nav>
    );
};

// --- Context Menu ---
const ContextMenu = ({ x, y, item, isAttached, onOpen, onDownload, onAttach, onRename, onDelete, onEnter, onClose }) => {
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
        };
        const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEsc);
        };
    }, [onClose]);

    // Clamp position to viewport
    const style = { position: 'fixed', zIndex: 9999 };
    style.left = Math.min(x, window.innerWidth - 200);
    style.top = Math.min(y, window.innerHeight - 250);

    return (
        <div className="fe-context-menu" style={style} ref={menuRef}>
            {item.type === 'dir' ? (
                <button className="fe-ctx-item" onClick={() => { onEnter(item); onClose(); }}>Open Folder</button>
            ) : (
                <>
                    <button className="fe-ctx-item" onClick={() => { onOpen(item); onClose(); }}>Open</button>
                    <button className="fe-ctx-item" onClick={() => { onDownload(item); onClose(); }}>Download</button>
                    <button className="fe-ctx-item" onClick={() => { onAttach(item); onClose(); }}>
                        {isAttached ? 'Detach from Chat' : 'Attach to Chat'}
                    </button>
                </>
            )}
            <div className="fe-ctx-divider" />
            <button className="fe-ctx-item" onClick={() => { onRename(item); onClose(); }}>Rename</button>
            <button className="fe-ctx-item fe-ctx-danger" onClick={() => { onDelete(item); onClose(); }}>Delete</button>
        </div>
    );
};

// --- Confirmation Modal Styles ---
const confirmModalStyles = {
    content: {
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        borderRadius: '8px', padding: '20px', maxWidth: '400px', width: '90%',
        background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
        color: 'var(--text-primary)', boxShadow: '0 4px 15px rgba(0,0,0,0.5)',
        display: 'flex', flexDirection: 'column', zIndex: 6000,
    },
    overlay: { backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 5999 },
};

// ====================================================================
// FileExplorer Component
// ====================================================================
const FileExplorer = ({ authUsername, attachedFiles = [], onAttachmentsChange, initialViewerPath }) => {
    // --- Core State ---
    const [currentPath, setCurrentPath] = useState('.');
    const [items, setItems] = useState([]);
    const [isLoadingFiles, setIsLoadingFiles] = useState(false);
    const [fileError, setFileError] = useState('');
    const [lastClickedFile, setLastClickedFile] = useState(null);
    const fileInputRef = useRef(null);
    const [uploadStatus, setUploadStatus] = useState({ message: '', type: '' });
    const [directorySize, setDirectorySize] = useState({ bytes: 0, human: 'Calculating...' });
    const [isLoadingSize, setIsLoadingSize] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });

    // --- Search ---
    const [searchQuery, setSearchQuery] = useState('');

    // --- View Mode ---
    const [viewMode, setViewMode] = useState('list');

    // --- Drag & Drop ---
    const [isDragOver, setIsDragOver] = useState(false);
    const dragCounter = useRef(0);

    // --- Internal Drag & Drop (Move) ---
    const [draggedItem, setDraggedItem] = useState(null);
    const [dropTargetPath, setDropTargetPath] = useState(null);

    // --- Document Viewer ---
    const [isViewerOpen, setIsViewerOpen] = useState(false);
    const [viewingFilePath, setViewingFilePath] = useState(null);

    // --- Context Menu ---
    const [contextMenu, setContextMenu] = useState(null);

    // --- Confirmation Modal ---
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [actionToConfirm, setActionToConfirm] = useState(null);

    // --- Rename ---
    const [renamingItem, setRenamingItem] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    const renameInputRef = useRef(null);

    // --- Open initial viewer path ---
    useEffect(() => {
        if (initialViewerPath) {
            setViewingFilePath(initialViewerPath);
            setIsViewerOpen(true);
        }
    }, [initialViewerPath]);

    // --- Data Fetching ---
    const fetchFiles = useCallback(async (relativePath) => {
        if (!authUsername) return;
        setIsLoadingFiles(true);
        setFileError('');
        setItems([]);
        setLastClickedFile(null);
        setSearchQuery('');
        try {
            const encodedPath = encodeURIComponent(relativePath);
            const response = await axios.get(`${BASE_URL}/api/file_explorer/list_user_files?path=${encodedPath}`, { withCredentials: true });
            setItems(response.data || []);
        } catch (error) {
            if (isPermissionDenied(error)) {
                setItems([]);
            } else {
                let errorMsg = `Failed to load files for '${relativePath}'.`;
                if (error.response?.status === 404) errorMsg = `Directory not found: '${relativePath}'.`;
                else if (error.response?.data?.error) errorMsg = error.response.data.error;
                else if (error.message) errorMsg += ` ${error.message}.`;
                setFileError(errorMsg);
                setItems([]);
            }
        } finally {
            setIsLoadingFiles(false);
        }
    }, [authUsername]);

    const fetchDirectorySize = useCallback(async () => {
        if (!authUsername) { setDirectorySize({ bytes: 0, human: 'N/A' }); return; }
        setIsLoadingSize(true);
        setDirectorySize(prev => ({ ...prev, human: 'Calculating...' }));
        try {
            const response = await axios.get(`${BASE_URL}/api/file_explorer/user_home_size`, { withCredentials: true });
            if (response.data && typeof response.data.size_bytes === 'number' && response.data.size_human) {
                setDirectorySize({ bytes: response.data.size_bytes, human: response.data.size_human });
            } else { throw new Error('Invalid size data'); }
        } catch (error) {
            if (isPermissionDenied(error)) {
                setDirectorySize({ bytes: 0, human: 'N/A' });
            } else {
                setDirectorySize({ bytes: 0, human: 'Error' });
            }
        } finally {
            setIsLoadingSize(false);
        }
    }, [authUsername]);

    // Initial load
    useEffect(() => {
        if (authUsername) {
            fetchFiles('.');
            fetchDirectorySize();
        }
    }, [authUsername, fetchFiles, fetchDirectorySize]);

    // Silent background refresh (doesn't clear state, just updates items)
    const silentRefresh = useCallback(async () => {
        if (!authUsername || isLoadingFiles) return;
        try {
            const encodedPath = encodeURIComponent(currentPath);
            const response = await axios.get(`${BASE_URL}/api/file_explorer/list_user_files?path=${encodedPath}`, { withCredentials: true });
            const newItems = response.data || [];
            setItems(prev => {
                // Only update if data actually changed (avoid unnecessary re-renders)
                if (JSON.stringify(prev) !== JSON.stringify(newItems)) return newItems;
                return prev;
            });
        } catch {
            // Silently ignore errors during background refresh
        }
    }, [authUsername, currentPath, isLoadingFiles]);

    // Auto-refresh polling (every 5 seconds)
    useEffect(() => {
        const interval = setInterval(silentRefresh, 5000);
        return () => clearInterval(interval);
    }, [silentRefresh]);

    // --- Sorting & Filtering ---
    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const filteredAndSortedItems = useMemo(() => {
        let result = [...items];

        // Filter by search
        if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase();
            result = result.filter(item => item.name.toLowerCase().includes(q));
        }

        // Sort
        if (sortConfig.key !== null) {
            result.sort((a, b) => {
                // Directories always first
                if (a.type === 'dir' && b.type !== 'dir') return -1;
                if (a.type !== 'dir' && b.type === 'dir') return 1;

                let valA = a[sortConfig.key];
                let valB = b[sortConfig.key];
                if (sortConfig.key === 'lastModified') {
                    const dateA = (valA && valA !== 'N/A') ? new Date(valA.replace(' ', 'T')) : null;
                    const dateB = (valB && valB !== 'N/A') ? new Date(valB.replace(' ', 'T')) : null;
                    if (!dateA && !dateB) return 0;
                    if (!dateA) return 1;
                    if (!dateB) return -1;
                    valA = dateA;
                    valB = dateB;
                }
                if (sortConfig.key === 'size') {
                    valA = valA ?? -1;
                    valB = valB ?? -1;
                }
                let comparison = 0;
                if (valA < valB) comparison = -1;
                else if (valA > valB) comparison = 1;
                return sortConfig.direction === 'ascending' ? comparison : -comparison;
            });
        }
        return result;
    }, [items, sortConfig, searchQuery]);

    // --- Path Helpers ---
    const buildFullPath = useCallback((basePath, itemName) => {
        if (basePath === '.') return itemName;
        const cleanBase = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
        return `${cleanBase}/${itemName}`;
    }, []);

    // --- Navigation ---
    const navigateTo = useCallback((path) => {
        setUploadStatus({ message: '', type: '' });
        setCurrentPath(path);
        fetchFiles(path);
        setSortConfig({ key: 'name', direction: 'ascending' });
    }, [fetchFiles]);

    const navigateBack = useCallback(() => {
        if (currentPath === '.') return;
        const pathParts = currentPath.split('/').filter(p => p && p !== '.');
        pathParts.pop();
        const newPath = pathParts.length > 0 ? pathParts.join('/') : '.';
        navigateTo(newPath);
    }, [currentPath, navigateTo]);

    // --- File Click Handlers ---
    const handleItemClick = (item) => {
        setLastClickedFile({ name: item.name, path: buildFullPath(currentPath, item.name), type: item.type });
    };

    const handleItemDoubleClick = (item) => {
        setUploadStatus({ message: '', type: '' });
        if (item.type === 'dir') {
            navigateTo(buildFullPath(currentPath, item.name));
        } else {
            // Open file in document viewer
            openFile(item);
        }
    };

    // --- Open File ---
    const openFile = useCallback((item) => {
        if (!item || item.type === 'dir' || !authUsername) return;
        const path = typeof item === 'string' ? item : (item.path || buildFullPath(currentPath, item.name));
        setViewingFilePath(path);
        setIsViewerOpen(true);
    }, [authUsername, currentPath, buildFullPath]);

    const handleOpenSelected = () => {
        if (!lastClickedFile || lastClickedFile.type === 'dir') return;
        openFile(lastClickedFile);
    };

    // --- Download File ---
    const downloadFile = useCallback(async (item) => {
        const filePath = item.path || buildFullPath(currentPath, item.name);
        try {
            const downloadUrl = `${BASE_URL}/api/file_explorer/download_user_file/${encodeURIComponent(filePath)}`;
            const response = await axios.get(downloadUrl, { withCredentials: true, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', item.name);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setUploadStatus({ message: `Failed to download '${item.name}'.`, type: 'error' });
            setTimeout(() => setUploadStatus({ message: '', type: '' }), 5000);
        }
    }, [currentPath, buildFullPath]);

    // --- Upload ---
    const uploadFiles = useCallback(async (fileList) => {
        if (!fileList || fileList.length === 0) return;
        setUploadStatus({ message: `Uploading ${fileList.length} file(s)...`, type: 'loading' });
        const formData = new FormData();
        for (let i = 0; i < fileList.length; i++) formData.append('files', fileList[i]);
        formData.append('relativePath', currentPath);
        try {
            const response = await axios.post(`${BASE_URL}/api/file_explorer/upload_user_files`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true,
            });
            setUploadStatus({ message: response.data.message || 'Upload successful!', type: 'success' });
            fetchFiles(currentPath);
            fetchDirectorySize();
        } catch (error) {
            if (isPermissionDenied(error)) {
                setUploadStatus({ message: '', type: '' });
            } else {
                const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || 'Upload failed.';
                setUploadStatus({ message: errorMsg, type: 'error' });
            }
        } finally {
            if (fileInputRef.current) fileInputRef.current.value = '';
            setTimeout(() => setUploadStatus({ message: '', type: '' }), 5000);
        }
    }, [currentPath, fetchFiles, fetchDirectorySize]);

    const handleFileInputChange = (event) => {
        uploadFiles(event.target.files);
    };

    const handleUploadClick = () => {
        setUploadStatus({ message: '', type: '' });
        fileInputRef.current?.click();
    };

    // --- Drag & Drop ---
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current++;
        // Only show upload overlay for external files, not internal moves
        if (e.dataTransfer.types.includes('Files') && !e.dataTransfer.types.includes('application/x-fe-move')) {
            setIsDragOver(true);
        }
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current--;
        if (dragCounter.current === 0) setIsDragOver(false);
    };
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        dragCounter.current = 0;
        // Only handle external file uploads, not internal moves
        if (e.dataTransfer.types.includes('application/x-fe-move')) return;
        if (e.dataTransfer.files.length > 0) uploadFiles(e.dataTransfer.files);
    };

    // --- Internal Drag & Drop (Move Files) ---
    const moveFile = useCallback(async (sourcePath, destinationDir) => {
        setUploadStatus({ message: 'Moving item...', type: 'loading' });
        try {
            await axios.post(`${BASE_URL}/api/file_explorer/move_file`, {
                sourcePath,
                destinationDir,
            }, { withCredentials: true });
            setUploadStatus({ message: 'Item moved successfully.', type: 'success' });
            // Update attachments if the moved file was attached
            if (attachedFiles.includes(sourcePath)) {
                const fileName = sourcePath.split('/').pop();
                const newPath = destinationDir === '.' || destinationDir === '' ? fileName : `${destinationDir}/${fileName}`;
                onAttachmentsChange(attachedFiles.map(p => p === sourcePath ? newPath : p));
            }
            fetchFiles(currentPath);
            fetchDirectorySize();
        } catch (error) {
            if (isPermissionDenied(error)) {
                setUploadStatus({ message: '', type: '' });
            } else {
                const errorMsg = error.response?.data?.error || error.message || 'Move failed.';
                setUploadStatus({ message: errorMsg, type: 'error' });
            }
        } finally {
            setTimeout(() => setUploadStatus({ message: '', type: '' }), 5000);
        }
    }, [currentPath, fetchFiles, fetchDirectorySize, attachedFiles, onAttachmentsChange]);

    const handleInternalDragStart = useCallback((e, item) => {
        const fullPath = buildFullPath(currentPath, item.name);
        setDraggedItem({ ...item, path: fullPath });
        e.dataTransfer.setData('application/x-fe-move', fullPath);
        e.dataTransfer.effectAllowed = 'move';
    }, [currentPath, buildFullPath]);

    const handleInternalDragEnd = useCallback(() => {
        setDraggedItem(null);
        setDropTargetPath(null);
    }, []);

    const handleFolderDragOver = useCallback((e, folderPath) => {
        if (e.dataTransfer.types.includes('application/x-fe-move')) {
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'move';
            setDropTargetPath(folderPath);
        }
    }, []);

    const handleFolderDragLeave = useCallback((e) => {
        e.stopPropagation();
        setDropTargetPath(null);
    }, []);

    const handleFolderDrop = useCallback((e, targetDirPath) => {
        e.preventDefault();
        e.stopPropagation();
        setDropTargetPath(null);
        const sourcePath = e.dataTransfer.getData('application/x-fe-move');
        if (!sourcePath || sourcePath === targetDirPath) return;
        // Prevent dropping into the same parent directory
        const sourceDir = sourcePath.includes('/') ? sourcePath.substring(0, sourcePath.lastIndexOf('/')) : '.';
        const normalizedTarget = targetDirPath || '.';
        if (sourceDir === normalizedTarget) return;
        moveFile(sourcePath, normalizedTarget);
    }, [moveFile]);

    const handleBreadcrumbDrop = useCallback((e, targetPath) => {
        e.preventDefault();
        e.stopPropagation();
        setDropTargetPath(null);
        const sourcePath = e.dataTransfer.getData('application/x-fe-move');
        if (!sourcePath) return;
        const sourceDir = sourcePath.includes('/') ? sourcePath.substring(0, sourcePath.lastIndexOf('/')) : '.';
        const normalizedTarget = targetPath || '.';
        if (sourceDir === normalizedTarget) return;
        moveFile(sourcePath, normalizedTarget);
    }, [moveFile]);

    const handleBreadcrumbDragOver = useCallback((e) => {
        if (e.dataTransfer.types.includes('application/x-fe-move')) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
        }
    }, []);

    // --- Delete ---
    const handleDeleteClick = (item) => {
        const target = item || lastClickedFile;
        if (!target) return;
        setActionToConfirm({
            type: 'delete',
            item: target,
            message: `Are you sure you want to permanently delete '${target.name}'? This action cannot be undone.`,
        });
        setShowConfirmModal(true);
    };

    const handleConfirmAction = async () => {
        if (!actionToConfirm) return;
        if (actionToConfirm.type === 'delete') {
            const { item } = actionToConfirm;
            const pathToDelete = item.path || buildFullPath(currentPath, item.name);
            setUploadStatus({ message: `Deleting '${item.name}'...`, type: 'loading' });
            try {
                await axios.post(`${BASE_URL}/api/file_explorer/delete_user_files`, { filePaths: [pathToDelete] }, { withCredentials: true });
                setUploadStatus({ message: 'Item deleted.', type: 'success' });
                setLastClickedFile(null);
                // Also remove from attachments if attached
                if (attachedFiles.includes(pathToDelete)) {
                    onAttachmentsChange(attachedFiles.filter(p => p !== pathToDelete));
                }
                fetchFiles(currentPath);
                fetchDirectorySize();
            } catch (error) {
                if (isPermissionDenied(error)) {
                    setUploadStatus({ message: '', type: '' });
                } else {
                    const errorMsg = error.response?.data?.error || error.message || 'Failed to delete item.';
                    setUploadStatus({ message: errorMsg, type: 'error' });
                }
            } finally {
                setTimeout(() => setUploadStatus({ message: '', type: '' }), 5000);
            }
        }
        setActionToConfirm(null);
        setShowConfirmModal(false);
    };

    // --- Rename ---
    const startRename = (item) => {
        const target = item || lastClickedFile;
        if (!target) return;
        setRenamingItem(target);
        setRenameValue(target.name);
        setTimeout(() => renameInputRef.current?.select(), 50);
    };

    const handleRenameSubmit = async () => {
        if (!renamingItem || !renameValue.trim()) {
            setRenamingItem(null);
            return;
        }
        if (renameValue.trim() === renamingItem.name) {
            setRenamingItem(null);
            return;
        }
        const filePath = renamingItem.path || buildFullPath(currentPath, renamingItem.name);
        try {
            await axios.post(`${BASE_URL}/api/file_explorer/rename_file`, {
                filePath, newName: renameValue.trim(),
            }, { withCredentials: true });
            setUploadStatus({ message: `Renamed to '${renameValue.trim()}'.`, type: 'success' });
            // Update attachments if the renamed file was attached
            if (attachedFiles.includes(filePath)) {
                const newPath = buildFullPath(currentPath, renameValue.trim());
                onAttachmentsChange(attachedFiles.map(p => p === filePath ? newPath : p));
            }
            fetchFiles(currentPath);
        } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || 'Rename failed.';
            setUploadStatus({ message: errorMsg, type: 'error' });
        } finally {
            setRenamingItem(null);
            setTimeout(() => setUploadStatus({ message: '', type: '' }), 5000);
        }
    };

    const handleRenameKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); handleRenameSubmit(); }
        else if (e.key === 'Escape') setRenamingItem(null);
    };

    // --- Attach/Detach ---
    const toggleAttach = (item) => {
        const fullPath = item.path || buildFullPath(currentPath, item.name);
        const newAttached = attachedFiles.includes(fullPath)
            ? attachedFiles.filter(p => p !== fullPath)
            : [...attachedFiles, fullPath];
        onAttachmentsChange(newAttached);
    };

    // --- Context Menu ---
    const handleContextMenu = (e, item) => {
        e.preventDefault();
        const fullPath = buildFullPath(currentPath, item.name);
        setContextMenu({ x: e.clientX, y: e.clientY, item: { ...item, path: fullPath } });
    };

    // --- Render ---
    return (
        <ProtectedElement required={['file-explorer:list', 'file-explorer:preview']} behavior="disable" fallbackVariant="overlay">
            <div className="fe-container">
                {/* Toolbar Row 1: Breadcrumbs + Size */}
                <div className="fe-toolbar-breadcrumb-row">
                    <button
                        className="fe-btn fe-btn-back"
                        onClick={navigateBack}
                        disabled={currentPath === '.' || isLoadingFiles}
                        title="Go back (or drop here to move to parent)"
                        onDragOver={currentPath !== '.' ? handleBreadcrumbDragOver : undefined}
                        onDrop={currentPath !== '.' ? (e) => {
                            const parentPath = currentPath.includes('/') ? currentPath.substring(0, currentPath.lastIndexOf('/')) : '.';
                            handleBreadcrumbDrop(e, parentPath);
                        } : undefined}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16"><path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8"/></svg>
                    </button>
                    <Breadcrumbs currentPath={currentPath} onNavigate={navigateTo} onDrop={handleBreadcrumbDrop} onDragOver={handleBreadcrumbDragOver} />
                    <span className="fe-size-info" title={`Total size: ${directorySize.bytes} bytes`}>
                        {isLoadingSize ? 'Calculating...' : directorySize.human}
                    </span>
                </div>

                {/* Toolbar Row 2: Actions */}
                <div className="fe-toolbar">
                    <div className="fe-toolbar-actions">
                        <ProtectedElement required="file-explorer:upload" behavior="disable" disabledTooltip="Requires file-explorer:upload">
                            <button className="fe-btn" onClick={handleUploadClick} disabled={isLoadingFiles || uploadStatus.type === 'loading'} title="Upload files">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16"><path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5"/><path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708z"/></svg>
                                Upload
                            </button>
                        </ProtectedElement>
                        <ProtectedElement required="file-explorer:preview" behavior="disable" disabledTooltip="Requires file-explorer:preview">
                            <button className="fe-btn" onClick={handleOpenSelected} disabled={!lastClickedFile || lastClickedFile.type === 'dir' || isLoadingFiles} title="Open selected file">
                                Open
                            </button>
                        </ProtectedElement>
                        <ProtectedElement required="file-explorer:delete" behavior="disable" disabledTooltip="Requires file-explorer:delete">
                            <button className="fe-btn fe-btn-danger" onClick={() => handleDeleteClick()} disabled={!lastClickedFile || isLoadingFiles} title="Delete selected item">
                                Delete
                            </button>
                        </ProtectedElement>
                    </div>

                    <div className="fe-toolbar-right">
                        <div className="fe-search">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="fe-search-icon" viewBox="0 0 16 16"><path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/></svg>
                            <input
                                type="text"
                                placeholder="Filter files..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="fe-search-input"
                            />
                            {searchQuery && (
                                <button onClick={() => setSearchQuery('')} className="fe-search-clear" title="Clear search">x</button>
                            )}
                        </div>
                        <span className="fe-attachment-count" title={`${attachedFiles.length} file(s) attached to chat`}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16"><path d="M4.5 3a2.5 2.5 0 0 1 5 0v9a1.5 1.5 0 0 1-3 0V5a.5.5 0 0 1 1 0v7a.5.5 0 0 0 1 0V3a1.5 1.5 0 1 0-3 0v9a2.5 2.5 0 0 0 5 0V5a.5.5 0 0 1 1 0v7a3.5 3.5 0 1 1-7 0z"/></svg>
                            {attachedFiles.length}
                        </span>
                        <select
                            className="fe-sort-select"
                            value={`${sortConfig.key}-${sortConfig.direction}`}
                            onChange={(e) => {
                                const [key, direction] = e.target.value.split('-');
                                setSortConfig({ key, direction });
                            }}
                            title="Sort files"
                        >
                            <option value="name-ascending">Name A-Z</option>
                            <option value="name-descending">Name Z-A</option>
                            <option value="lastModified-descending">Newest first</option>
                            <option value="lastModified-ascending">Oldest first</option>
                            <option value="size-descending">Largest first</option>
                            <option value="size-ascending">Smallest first</option>
                        </select>
                        <div className="fe-view-toggle">
                            <button
                                className={`fe-btn fe-btn-icon ${viewMode === 'list' ? 'fe-btn-active' : ''}`}
                                onClick={() => setViewMode('list')}
                                title="List view"
                            ><ListViewIcon /></button>
                            <button
                                className={`fe-btn fe-btn-icon ${viewMode === 'grid' ? 'fe-btn-active' : ''}`}
                                onClick={() => setViewMode('grid')}
                                title="Grid view"
                            ><GridViewIcon /></button>
                        </div>
                    </div>
                    <input type="file" multiple ref={fileInputRef} onChange={handleFileInputChange} className="fe-hidden-input" />
                </div>

                {/* Column Headers (list view only) */}
                {viewMode === 'list' && (
                    <div className="fe-list-headers">
                        <div className="fe-header fe-header-name" onClick={() => requestSort('name')}>
                            Name {sortConfig.key === 'name' && (sortConfig.direction === 'ascending' ? <SortAscIcon /> : <SortDescIcon />)}
                        </div>
                        <div className="fe-header fe-header-size" onClick={() => requestSort('size')}>
                            Size {sortConfig.key === 'size' && (sortConfig.direction === 'ascending' ? <SortAscIcon /> : <SortDescIcon />)}
                        </div>
                        <div className="fe-header fe-header-modified" onClick={() => requestSort('lastModified')}>
                            Modified {sortConfig.key === 'lastModified' && (sortConfig.direction === 'ascending' ? <SortAscIcon /> : <SortDescIcon />)}
                        </div>
                        <div className="fe-header fe-header-attached">
                            Attached
                        </div>
                    </div>
                )}

                {/* File List / Grid */}
                <div
                    className={`fe-file-area ${isDragOver ? 'fe-drag-over' : ''}`}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                >
                    {isDragOver && (
                        <div className="fe-drop-overlay">
                            <div className="fe-drop-overlay-content">
                                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" viewBox="0 0 16 16"><path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5"/><path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708z"/></svg>
                                <span>Drop files here to upload</span>
                            </div>
                        </div>
                    )}

                    {uploadStatus.message && (
                        <div className={`fe-status fe-status-${uploadStatus.type}`}>{uploadStatus.message}</div>
                    )}
                    {fileError && <div className="fe-status fe-status-error">{fileError}</div>}
                    {isLoadingFiles && <div className="fe-status fe-status-loading">Loading files...</div>}

                    {!isLoadingFiles && filteredAndSortedItems.length === 0 && !fileError && (
                        <div className="fe-empty-state">
                            <FolderIcon size={48} />
                            <p className="fe-empty-title">
                                {searchQuery ? 'No files match your search' : 'This folder is empty'}
                            </p>
                            <p className="fe-empty-subtitle">
                                {searchQuery
                                    ? `No results for "${searchQuery}"`
                                    : 'Drop files here or click Upload to add files'}
                            </p>
                        </div>
                    )}

                    {viewMode === 'list' ? (
                        <ul className="fe-file-list">
                            {filteredAndSortedItems.map((item) => {
                                const fullPath = buildFullPath(currentPath, item.name);
                                const isAttached = attachedFiles.includes(fullPath);
                                const isSelected = lastClickedFile?.path === fullPath;
                                const isRenaming = renamingItem?.name === item.name;
                                return (
                                    <li
                                        key={`${item.name}-${item.lastModified || 'no-date'}`}
                                        className={`fe-file-item ${item.type} ${isAttached ? 'fe-attached' : ''} ${isSelected ? 'fe-selected' : ''} ${draggedItem?.path === fullPath ? 'fe-dragging' : ''} ${dropTargetPath === fullPath && item.type === 'dir' ? 'fe-drop-target' : ''}`}
                                        onClick={() => handleItemClick(item)}
                                        onDoubleClick={() => handleItemDoubleClick(item)}
                                        onContextMenu={(e) => handleContextMenu(e, item)}
                                        draggable={!isRenaming}
                                        onDragStart={(e) => handleInternalDragStart(e, item)}
                                        onDragEnd={handleInternalDragEnd}
                                        onDragOver={item.type === 'dir' ? (e) => handleFolderDragOver(e, fullPath) : undefined}
                                        onDragLeave={item.type === 'dir' ? handleFolderDragLeave : undefined}
                                        onDrop={item.type === 'dir' ? (e) => handleFolderDrop(e, fullPath) : undefined}
                                        title={`${item.name}\nType: ${item.type}\n${item.size != null ? `Size: ${formatFileSize(item.size)}\n` : ''}Modified: ${item.lastModified || 'N/A'}\nDouble-click to ${item.type === 'dir' ? 'enter' : 'open'}`}
                                    >
                                        <span className="fe-item-name-col">
                                            {getFileIcon(item.name, item.type)}
                                            {isRenaming ? (
                                                <input
                                                    ref={renameInputRef}
                                                    className="fe-rename-input"
                                                    value={renameValue}
                                                    onChange={(e) => setRenameValue(e.target.value)}
                                                    onBlur={handleRenameSubmit}
                                                    onKeyDown={handleRenameKeyDown}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onDoubleClick={(e) => e.stopPropagation()}
                                                    autoFocus
                                                />
                                            ) : (
                                                <span className="fe-item-name">{item.name}</span>
                                            )}
                                        </span>
                                        <span className="fe-item-size">{formatFileSize(item.size)}</span>
                                        <span className="fe-item-modified">{item.lastModified && item.lastModified !== 'N/A' ? item.lastModified : ''}</span>
                                        <span className="fe-item-attached">{isAttached ? '\u2713' : ''}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <div className="fe-file-grid">
                            {filteredAndSortedItems.map((item) => {
                                const fullPath = buildFullPath(currentPath, item.name);
                                const isAttached = attachedFiles.includes(fullPath);
                                const isSelected = lastClickedFile?.path === fullPath;
                                const isRenaming = renamingItem?.name === item.name;
                                return (
                                    <div
                                        key={`${item.name}-${item.lastModified || 'no-date'}`}
                                        className={`fe-grid-tile ${item.type} ${isAttached ? 'fe-attached' : ''} ${isSelected ? 'fe-selected' : ''} ${draggedItem?.path === fullPath ? 'fe-dragging' : ''} ${dropTargetPath === fullPath && item.type === 'dir' ? 'fe-drop-target' : ''}`}
                                        onClick={() => handleItemClick(item)}
                                        onDoubleClick={() => handleItemDoubleClick(item)}
                                        onContextMenu={(e) => handleContextMenu(e, item)}
                                        draggable={!isRenaming}
                                        onDragStart={(e) => handleInternalDragStart(e, item)}
                                        onDragEnd={handleInternalDragEnd}
                                        onDragOver={item.type === 'dir' ? (e) => handleFolderDragOver(e, fullPath) : undefined}
                                        onDragLeave={item.type === 'dir' ? handleFolderDragLeave : undefined}
                                        onDrop={item.type === 'dir' ? (e) => handleFolderDrop(e, fullPath) : undefined}
                                        title={item.name}
                                    >
                                        {getFileIcon(item.name, item.type, 36)}
                                        {isRenaming ? (
                                            <input
                                                ref={renameInputRef}
                                                className="fe-rename-input fe-rename-grid"
                                                value={renameValue}
                                                onChange={(e) => setRenameValue(e.target.value)}
                                                onBlur={handleRenameSubmit}
                                                onKeyDown={handleRenameKeyDown}
                                                onClick={(e) => e.stopPropagation()}
                                                onDoubleClick={(e) => e.stopPropagation()}
                                                autoFocus
                                            />
                                        ) : (
                                            <span className="fe-grid-name">{item.name}</span>
                                        )}
                                        {isAttached && <span className="fe-grid-attached-badge">\u2713</span>}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Context Menu */}
                {contextMenu && (
                    <ContextMenu
                        x={contextMenu.x}
                        y={contextMenu.y}
                        item={contextMenu.item}
                        isAttached={attachedFiles.includes(contextMenu.item.path)}
                        onOpen={openFile}
                        onDownload={downloadFile}
                        onAttach={toggleAttach}
                        onRename={startRename}
                        onDelete={handleDeleteClick}
                        onEnter={(item) => navigateTo(item.path || buildFullPath(currentPath, item.name))}
                        onClose={() => setContextMenu(null)}
                    />
                )}

                {/* Confirmation Modal */}
                <Modal
                    isOpen={showConfirmModal}
                    onRequestClose={() => { setActionToConfirm(null); setShowConfirmModal(false); }}
                    style={confirmModalStyles}
                    contentLabel="Confirm Action"
                    ariaHideApp={false}
                >
                    <div className="fe-confirm-header">
                        <h3>Confirm Action</h3>
                        <button onClick={() => { setActionToConfirm(null); setShowConfirmModal(false); }} className="fe-confirm-close">x</button>
                    </div>
                    <div className="fe-confirm-body">
                        <p>{actionToConfirm?.message || 'Are you sure?'}</p>
                    </div>
                    <div className="fe-confirm-actions">
                        <button onClick={handleConfirmAction} className="fe-btn fe-btn-danger">Yes, Proceed</button>
                        <button onClick={() => { setActionToConfirm(null); setShowConfirmModal(false); }} className="fe-btn">Cancel</button>
                    </div>
                </Modal>

                {/* Document Viewer */}
                <DocumentViewerModal
                    isOpen={isViewerOpen}
                    onClose={() => { setIsViewerOpen(false); setViewingFilePath(null); }}
                    filePath={viewingFilePath}
                    username={authUsername}
                />
            </div>
        </ProtectedElement>
    );
};

export default FileExplorer;
